import Phaser from "phaser";
import { assetUrl } from "../assetVer.js";

/**
 * Item icons, drawn programmatically as 16x16 pixel art. One source of truth for
 * BOTH the Phaser world (ground-item textures) and the DOM inventory panel
 * (canvas thumbnails) — mirroring the canvas-crop technique in characterCreate.ts,
 * but generated rather than sliced from a sheet (no asset files, no fragile crop
 * coordinates). Swap these for real sprite crops later by changing only this file.
 *
 * Exception: a few organic items (IMAGE_ICONS) use finer 32px PNG art from
 * /assets/items/ instead — shapes the chunky 16px grid can't carry. Preloaded at
 * module init so the icon canvas can almost always be painted synchronously.
 */

export const ICON_SIZE = 16;

/** Items whose icon is a 32px PNG asset rather than procedural 16px art. */
const IMAGE_ICONS: Record<string, string> = {
  weed_seed: "/assets/items/weed_seed.png",
  weed_bud: "/assets/items/weed_bud.png",
};
const iconImages = new Map<string, HTMLImageElement>();
for (const [id, path] of Object.entries(IMAGE_ICONS)) {
  const img = new Image();
  img.src = assetUrl(path);
  iconImages.set(id, img);
}

/** A 32×32 canvas painted from a preloaded PNG icon (async-painted if not yet cached). */
function imageIcon(itemId: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 32;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  const img = iconImages.get(itemId)!;
  const paint = (): void => { ctx.drawImage(img, 0, 0, 32, 32); };
  if (img.complete && img.naturalWidth > 0) paint();
  else img.addEventListener("load", paint, { once: true });
  return canvas;
}

/** Draw the icon for an item id into a fresh pixelated 16x16 canvas. */
export function drawItemIcon(itemId: string): HTMLCanvasElement {
  if (IMAGE_ICONS[itemId]) return imageIcon(itemId); // 32px PNG icons (seed / bud)
  const canvas = document.createElement("canvas");
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  switch (itemId) {
    case "black_shirt":
      drawShirt(ctx);
      break;
    case "blue_jeans":
      drawJeans(ctx);
      break;
    case "black_cap":
      drawCap(ctx);
      break;
    case "black_shoes":
      drawShoes(ctx);
      break;
    case "ghost_tee":
      drawGhostTee(ctx);
      break;
    case "work_pants":
      drawWorkPants(ctx);
      break;
    case "pickaxe_tee":
      drawPickaxeTee(ctx);
      break;
    case "coin":
      drawCoin(ctx);
      break;
    case "cash":
      drawCash(ctx);
      break;
    case "ghost_scroll":
      drawScroll(ctx);
      break;
    case "ore_gold":
      drawOre(ctx, "#e8b53a", "#f6dd84");
      break;
    case "ore_silver":
      drawOre(ctx, "#b9c2cc", "#e8eef4");
      break;
    case "ore_copper":
      drawOre(ctx, "#b06a3a", "#e08a4a");
      break;
    case "ore_iron":
      drawOre(ctx, "#9a6a4a", "#c08a64");
      break;
    case "ore_stone":
      drawOre(ctx, "#6b6b73", "#9a9aa2");
      break;
    case "pickaxe":
      drawPickaxe(ctx);
      break;
    case "grass_cutter":
      drawShears(ctx);
      break;
    case "sword":
      drawSword(ctx);
      break;
    case "round_shield":
      drawShield(ctx);
      break;
    case "skinning_knife":
      drawKnife(ctx);
      break;
    case "hog_hide":
      drawHide(ctx);
      break;
    case "leather_low":
      drawLeather(ctx);
      break;
    case "copper_bar":
      drawMetalBar(ctx, "#b06a3a", "#e08a4a", "#7a4422");
      break;
    case "iron_bar":
      drawMetalBar(ctx, "#8a9099", "#c9d2da", "#565c66");
      break;
    case "silver_bar":
      drawMetalBar(ctx, "#b9c2cc", "#eef3f8", "#7d8791");
      break;
    case "gold_bar":
      drawMetalBar(ctx, "#e8b53a", "#f6dd84", "#a87b1c");
      break;
    case "leather_top_str":
    case "leather_top_dex":
    case "leather_top_agi":
    case "armored_leather_top_str":
    case "armored_leather_top_dex":
    case "armored_leather_top_agi":
      drawLeatherTop(ctx, itemId.startsWith("armored_"));
      break;
    case "leather_pants_str":
    case "leather_pants_dex":
    case "leather_pants_agi":
    case "armored_leather_pants_str":
    case "armored_leather_pants_dex":
    case "armored_leather_pants_agi":
      drawLeatherPants(ctx, itemId.startsWith("armored_"));
      break;
    case "blunt":
      drawBlunt(ctx);
      break;
    case "hog_burger":
      drawHogBurger(ctx);
      break;
    case "potato":
      drawPotato(ctx);
      break;
    case "fries":
      drawFries(ctx);
      break;
    case "water":
      drawDrink(ctx, "#9fdcef", "#d8f3fb");
      break;
    case "soda":
      drawDrink(ctx, "#d23a3a", "#ff7a7a");
      break;
    case "hog_meat":
      drawMeat(ctx);
      break;
    case "hog_charm":
      drawCharm(ctx);
      break;
    case "hog_tusk":
      drawTusk(ctx);
      break;
    case "book_running":
    case "book_goblin_tech":
      drawBook(ctx);
      break;
    case "goblin_ear":
      drawTusk(ctx);
      break;
    case "magic_dust":
      drawMagicDust(ctx);
      break;
    case "fishing_rod":
      drawRod(ctx);
      break;
    case "worms":
      drawWorms(ctx);
      break;
    case "mudfish":
      drawFish(ctx, { body: "#8f9aa4", belly: "#c2cad2", dark: "#5f6770", fin: "#737d87", gleam: "#dfe6ec" });
      break;
    case "pondfish":
      drawFish(ctx, { body: "#c28e54", belly: "#e8c99a", dark: "#8a5f33", fin: "#a9773f", gleam: "#f5e2c0" });
      break;
    case "moss_perch":
      drawFish(ctx, { body: "#4a8a6d", belly: "#9ad4b3", dark: "#2f5c47", fin: "#3d7159", gleam: "#c9f0da" }, { dorsal: true, spots: "#2f5c47" });
      break;
    case "sun_carp":
      drawFish(ctx, { body: "#e6b23a", belly: "#ffe9a8", dark: "#a87b1c", fin: "#d09a28", gleam: "#fff6d0" }, { dorsal: true, glow: "#ffefb0" });
      break;
    case "bog_carp":
      drawFish(ctx, { body: "#5e8a45", belly: "#8fbf63", dark: "#3c5b2d", fin: "#4a6f37", gleam: "#cdeba0" }, { dorsal: true, spots: "#3c5b2d" });
      break;
    case "ghost_koi":
      drawFish(ctx, { body: "#7fd2e6", belly: "#dffaff", dark: "#3f9bbd", fin: "#5cb8d6", gleam: "#f0ffff" }, { dorsal: true, glow: "#eaffff" });
      break;
    case "bmx":
      drawBmxIcon(ctx);
      break;
    case "bed":
      drawBed(ctx);
      break;
    case "safebox":
      drawSafebox(ctx);
      break;
    case "furnace":
      drawFurnace(ctx);
      break;
    case "anvil":
      drawAnvil(ctx);
      break;
    case "watering_pot":
      drawWateringPot(ctx);
      break;
    case "rolling_paper":
      drawRollingPaper(ctx);
      break;
    default:
      drawUnknown(ctx);
  }
  return canvas;
}

/**
 * Ensure a Phaser texture `item-<id>` exists for the given item, generating it
 * from `drawItemIcon`. Safe to call repeatedly. Returns the texture key.
 */
export function ensureItemTexture(scene: Phaser.Scene, itemId: string): string {
  const key = `item-${itemId}`;
  if (!scene.textures.exists(key)) {
    scene.textures.addCanvas(key, drawItemIcon(itemId));
  }
  return key;
}

function drawShirt(ctx: CanvasRenderingContext2D): void {
  // Black short-sleeve tee.
  ctx.fillStyle = "#26262b";
  ctx.fillRect(4, 5, 8, 9);   // torso
  ctx.fillRect(2, 5, 2, 4);   // left sleeve
  ctx.fillRect(12, 5, 2, 4);  // right sleeve
  ctx.fillRect(5, 4, 6, 1);   // shoulders
  ctx.fillStyle = "#3b3b42";  // collar
  ctx.fillRect(6, 4, 4, 1);
  ctx.fillStyle = "#0f0f12";  // neck hole
  ctx.fillRect(7, 4, 2, 1);
  ctx.fillStyle = "#3b3b42";  // highlight
  ctx.fillRect(5, 6, 1, 6);
}

function drawJeans(ctx: CanvasRenderingContext2D): void {
  // Blue denim pants.
  ctx.fillStyle = "#3a5da8";
  ctx.fillRect(4, 3, 8, 3);   // waist
  ctx.fillRect(4, 6, 3, 10);  // left leg
  ctx.fillRect(9, 6, 3, 10);  // right leg
  ctx.fillStyle = "#2a3f70";  // belt + shadow gap
  ctx.fillRect(4, 3, 8, 1);
  ctx.fillRect(7, 6, 2, 9);
  ctx.fillStyle = "#5a7fc8";  // highlight
  ctx.fillRect(5, 7, 1, 8);
}

function drawCap(ctx: CanvasRenderingContext2D): void {
  // Black baseball cap (dome + forward brim).
  ctx.fillStyle = "#26262b";
  ctx.fillRect(5, 3, 6, 1);   // crown top
  ctx.fillRect(4, 4, 8, 4);   // dome
  ctx.fillRect(3, 7, 9, 2);   // dome base
  ctx.fillRect(3, 9, 12, 2);  // brim
  ctx.fillStyle = "#3b3b42";  // button + highlight
  ctx.fillRect(7, 2, 2, 1);
  ctx.fillRect(5, 5, 1, 3);
  ctx.fillStyle = "#15151a";  // brim underside
  ctx.fillRect(3, 11, 12, 1);
}

function drawShoes(ctx: CanvasRenderingContext2D): void {
  // A pair of black sneakers with white soles.
  ctx.fillStyle = "#26262b";
  ctx.fillRect(2, 8, 5, 4);   // left shoe
  ctx.fillRect(2, 10, 6, 2);  // left toe
  ctx.fillRect(9, 8, 5, 4);   // right shoe
  ctx.fillRect(8, 10, 6, 2);  // right toe
  ctx.fillStyle = "#3b3b42";  // laces highlight
  ctx.fillRect(4, 9, 2, 1);
  ctx.fillRect(11, 9, 2, 1);
  ctx.fillStyle = "#e8e8ec";  // white soles
  ctx.fillRect(2, 12, 6, 1);
  ctx.fillRect(8, 12, 6, 1);
}

function drawCash(ctx: CanvasRenderingContext2D): void {
  // A small stack of green banknotes with a "$" — a dropped pile of cash.
  ctx.fillStyle = "#176b35";  // bottom note (shadow)
  ctx.fillRect(2, 9, 12, 4);
  ctx.fillStyle = "#1d7d3f";  // middle note
  ctx.fillRect(2, 7, 12, 4);
  ctx.fillStyle = "#2a9b53";  // top note face
  ctx.fillRect(2, 4, 12, 5);
  ctx.fillStyle = "#6fc98c";  // top edge highlight
  ctx.fillRect(2, 4, 12, 1);
  ctx.fillStyle = "#c4edd2";  // inner panel
  ctx.fillRect(4, 5, 8, 3);
  ctx.fillStyle = "#176b35";  // $ glyph
  ctx.fillRect(7, 5, 2, 3);
  ctx.fillRect(6, 5, 1, 1);
  ctx.fillRect(9, 7, 1, 1);
}

function drawCoin(ctx: CanvasRenderingContext2D): void {
  // Gold coin with a rim and a "$" notch, rendered as a chunky octagon so it
  // reads as round at 16x16 without anti-aliasing.
  ctx.fillStyle = "#9a6b18"; // dark rim
  ctx.fillRect(5, 2, 6, 12);
  ctx.fillRect(2, 5, 12, 6);
  ctx.fillRect(4, 3, 8, 10);
  ctx.fillRect(3, 4, 10, 8);
  ctx.fillStyle = "#f3c969"; // face
  ctx.fillRect(5, 4, 6, 8);
  ctx.fillRect(4, 5, 8, 6);
  ctx.fillRect(6, 3, 4, 10);
  ctx.fillStyle = "#ffe9a8"; // highlight
  ctx.fillRect(5, 4, 2, 2);
  ctx.fillStyle = "#9a6b18"; // engraved mark
  ctx.fillRect(7, 5, 2, 1);
  ctx.fillRect(7, 5, 1, 5);
  ctx.fillRect(7, 9, 2, 1);
}

function drawGhostTee(ctx: CanvasRenderingContext2D): void {
  // Black tee with a small white Pac-Man ghost on the chest.
  ctx.fillStyle = "#26262b";
  ctx.fillRect(4, 5, 8, 9);   // torso
  ctx.fillRect(2, 5, 2, 4);   // sleeves
  ctx.fillRect(12, 5, 2, 4);
  ctx.fillRect(5, 4, 6, 1);   // shoulders
  // white ghost (dome + wavy feet) with black eyes
  ctx.fillStyle = "#f2f2f5";
  ctx.fillRect(6, 7, 4, 1);
  ctx.fillRect(5, 8, 6, 4);
  ctx.fillStyle = "#26262b";   // feet notches + eyes
  ctx.fillRect(6, 11, 1, 1); ctx.fillRect(8, 11, 1, 1); ctx.fillRect(10, 11, 1, 1);
  ctx.fillRect(6, 9, 1, 1); ctx.fillRect(9, 9, 1, 1);
}

function drawWorkPants(ctx: CanvasRenderingContext2D): void {
  // Baggy black workpants with a sliver of blue underwear at the waist.
  ctx.fillStyle = "#3a5da8"; // blue underwear band peeking at the top
  ctx.fillRect(5, 3, 6, 1);
  ctx.fillStyle = "#1c1c20"; // black waist + wide legs (flared)
  ctx.fillRect(4, 4, 8, 3);
  ctx.fillRect(2, 7, 5, 8);   // wide left leg
  ctx.fillRect(9, 7, 5, 8);   // wide right leg
  ctx.fillStyle = "#2c2c32"; // highlight
  ctx.fillRect(5, 8, 1, 6);
}

function drawScroll(ctx: CanvasRenderingContext2D): void {
  // Rolled parchment scroll with a glowing teal teleport rune.
  ctx.fillStyle = "#caa86a"; // wooden end-rods
  ctx.fillRect(3, 2, 10, 2);
  ctx.fillRect(3, 12, 10, 2);
  ctx.fillStyle = "#9a7a3e"; // rod shadow
  ctx.fillRect(3, 3, 10, 1);
  ctx.fillRect(3, 13, 10, 1);
  ctx.fillStyle = "#efe2bd"; // parchment body
  ctx.fillRect(4, 4, 8, 8);
  ctx.fillStyle = "#d9c79a"; // parchment shade
  ctx.fillRect(4, 4, 1, 8);
  ctx.fillRect(11, 4, 1, 8);
  ctx.fillStyle = "#2fd0c8"; // glowing ghost-land rune
  ctx.fillRect(7, 6, 2, 1);
  ctx.fillRect(6, 7, 4, 1);
  ctx.fillRect(7, 8, 2, 2);
  ctx.fillStyle = "#9af6f0"; // rune highlight
  ctx.fillRect(7, 6, 1, 1);
}

function drawOre(ctx: CanvasRenderingContext2D, vein: string, gleam: string): void {
  // A chunky grey rock with coloured ore veins/flecks.
  ctx.fillStyle = "#55555c";
  ctx.fillRect(4, 6, 9, 7);
  ctx.fillRect(3, 8, 11, 4);
  ctx.fillRect(5, 5, 6, 1);
  ctx.fillStyle = "#3d3d44"; // shadow
  ctx.fillRect(4, 11, 9, 2);
  ctx.fillStyle = "#6f6f78"; // top highlight
  ctx.fillRect(5, 6, 4, 1);
  ctx.fillStyle = vein; // ore veins
  ctx.fillRect(6, 8, 2, 2);
  ctx.fillRect(9, 7, 2, 2);
  ctx.fillRect(10, 10, 1, 1);
  ctx.fillStyle = gleam; // gleam
  ctx.fillRect(6, 8, 1, 1);
  ctx.fillRect(9, 7, 1, 1);
}

function drawShield(ctx: CanvasRenderingContext2D): void {
  // A round wooden shield: dark rim, plank face, iron centre boss.
  ctx.fillStyle = "#3a2a18"; // rim
  for (const [x, y, w, h] of [[5, 2, 6, 1], [3, 4, 10, 1], [2, 5, 12, 6], [3, 11, 10, 1], [5, 13, 6, 1], [2, 6, 1, 4], [13, 6, 1, 4]]) ctx.fillRect(x, y, w, h);
  ctx.fillStyle = "#7a5a34"; // wood face
  ctx.fillRect(4, 4, 8, 8); ctx.fillRect(3, 5, 10, 6); ctx.fillRect(5, 3, 6, 10);
  ctx.fillStyle = "#9a7a4a"; // plank highlight
  ctx.fillRect(5, 4, 6, 1); ctx.fillRect(4, 6, 1, 4);
  ctx.fillStyle = "#6b7178"; // iron boss
  ctx.fillRect(7, 6, 3, 3); ctx.fillRect(6, 7, 1, 1); ctx.fillRect(10, 7, 1, 1);
  ctx.fillStyle = "#c7ccd2"; ctx.fillRect(7, 6, 1, 1); // boss glint
}

function drawSword(ctx: CanvasRenderingContext2D): void {
  // A diagonal iron sword: steel blade SW→NE, a crossguard, and a wrapped grip + pommel.
  ctx.fillStyle = "#c7ccd2"; // blade
  for (let i = 0; i < 9; i++) ctx.fillRect(5 + i, 9 - i, 2, 2);
  ctx.fillStyle = "#eef2f6"; // blade edge highlight
  for (let i = 0; i < 8; i++) ctx.fillRect(6 + i, 8 - i, 1, 1);
  ctx.fillStyle = "#6b7178"; // crossguard (perpendicular to the blade)
  ctx.fillRect(3, 10, 4, 1); ctx.fillRect(4, 11, 4, 1);
  ctx.fillStyle = "#7a4a22"; // grip
  ctx.fillRect(3, 11, 2, 2); ctx.fillRect(2, 12, 2, 2);
  ctx.fillStyle = "#d8b24a"; // pommel
  ctx.fillRect(1, 13, 2, 2);
}

function drawShears(ctx: CanvasRenderingContext2D): void {
  // Garden shears: two steel blades crossing at a pivot, with red grip handles below.
  ctx.fillStyle = "#c0c6cc"; // upper blade (diagonal, top-left → centre)
  for (let i = 0; i < 6; i++) ctx.fillRect(3 + i, 2 + i, 2, 2);
  ctx.fillStyle = "#aeb4ba"; // lower blade (top-right → centre)
  for (let i = 0; i < 6; i++) ctx.fillRect(11 - i, 2 + i, 2, 2);
  ctx.fillStyle = "#e6ebf0"; // blade gleam
  ctx.fillRect(4, 2, 1, 1); ctx.fillRect(11, 2, 1, 1);
  ctx.fillStyle = "#5a5e63"; // pivot bolt
  ctx.fillRect(7, 7, 2, 2);
  ctx.fillStyle = "#b23b3b"; // red grips (diagonal handles below the pivot)
  for (let i = 0; i < 5; i++) { ctx.fillRect(6 - i, 9 + i, 2, 2); ctx.fillRect(8 + i, 9 + i, 2, 2); }
  ctx.fillStyle = "#d65a5a"; // grip highlight
  ctx.fillRect(2, 13, 2, 1); ctx.fillRect(12, 13, 2, 1);
}

function drawPickaxeTee(ctx: CanvasRenderingContext2D): void {
  // White cotton tee with a small pickaxe printed on the chest.
  ctx.fillStyle = "#e4e4e8";  // white tee body
  ctx.fillRect(4, 5, 8, 9);   // torso
  ctx.fillRect(2, 5, 2, 4);   // sleeves
  ctx.fillRect(12, 5, 2, 4);
  ctx.fillRect(5, 4, 6, 1);   // shoulders
  ctx.fillStyle = "#f4f4f8";  // highlight
  ctx.fillRect(5, 6, 1, 6);
  // pickaxe print — diagonal wooden handle + a small steel head
  ctx.fillStyle = "#a9712f";
  for (let i = 0; i < 5; i++) ctx.fillRect(6 + i, 12 - i, 1, 1); // handle
  ctx.fillStyle = "#9aa0a6";  // steel head (small curved bar)
  ctx.fillRect(8, 6, 3, 1);
  ctx.fillRect(7, 7, 2, 1);
  ctx.fillRect(10, 7, 2, 1);
  ctx.fillStyle = "#c8cdd2";  // head gleam
  ctx.fillRect(9, 6, 2, 1);
}

function drawPickaxe(ctx: CanvasRenderingContext2D): void {
  // A wooden-handled pickaxe with a steel head.
  ctx.fillStyle = "#a9712f"; // handle (diagonal)
  for (let i = 0; i < 9; i++) ctx.fillRect(6 + i, 13 - i, 2, 2);
  ctx.fillStyle = "#c0c6cc"; // steel head (curved bar across the top)
  ctx.fillRect(3, 3, 10, 2);
  ctx.fillRect(2, 4, 3, 2);
  ctx.fillRect(11, 4, 3, 2);
  ctx.fillStyle = "#e6ebf0"; // head gleam
  ctx.fillRect(6, 3, 4, 1);
  ctx.fillStyle = "#7d8388"; // head shadow
  ctx.fillRect(3, 5, 10, 1);
}

function drawKnife(ctx: CanvasRenderingContext2D): void {
  // A curved skinning knife: brown handle (bottom-left) + a hooked steel blade.
  ctx.fillStyle = "#7a4a22"; // handle (diagonal)
  for (let i = 0; i < 4; i++) ctx.fillRect(3 + i, 12 - i, 2, 2);
  ctx.fillStyle = "#caa23c"; ctx.fillRect(5, 9, 2, 2); // brass bolster
  ctx.fillStyle = "#c0c6cc"; // blade sweeping up to the right
  for (let i = 0; i < 7; i++) ctx.fillRect(6 + i, 8 - i, 2, 2);
  ctx.fillRect(11, 1, 3, 2); // hooked tip
  ctx.fillStyle = "#e6ebf0"; // gleam
  ctx.fillRect(8, 5, 1, 1); ctx.fillRect(12, 2, 1, 1);
}

function drawHide(ctx: CanvasRenderingContext2D): void {
  // A stretched hog pelt: a light-brown hide with darker edges + a few bristle marks.
  ctx.fillStyle = "#9a6a44"; // outer edge
  ctx.fillRect(3, 3, 10, 10);
  ctx.fillStyle = "#c08a5e"; // hide body
  ctx.fillRect(4, 4, 8, 8);
  ctx.fillStyle = "#d8a878"; // light center
  ctx.fillRect(6, 6, 4, 4);
  ctx.fillStyle = "#6e4a30"; // corner pegs (stretched)
  ctx.fillRect(2, 2, 2, 2); ctx.fillRect(12, 2, 2, 2); ctx.fillRect(2, 12, 2, 2); ctx.fillRect(12, 12, 2, 2);
  ctx.fillStyle = "#5a3a26"; // bristle flecks
  ctx.fillRect(7, 5, 1, 1); ctx.fillRect(9, 8, 1, 1); ctx.fillRect(6, 9, 1, 1);
}

function drawLeather(ctx: CanvasRenderingContext2D): void {
  // A folded sheet of low-grade leather — green-tinted to read as a "low quality" (green) item,
  // with a darker rim, a fold seam, and a couple of stitch marks.
  ctx.fillStyle = "#3f6e3a"; // dark green rim
  ctx.fillRect(2, 3, 12, 10);
  ctx.fillStyle = "#5e9a4e"; // leather body (muted green)
  ctx.fillRect(3, 4, 10, 8);
  ctx.fillStyle = "#74b85f"; // lighter top sheet
  ctx.fillRect(3, 4, 10, 3);
  ctx.fillStyle = "#2f5630"; // fold seam across the middle
  ctx.fillRect(3, 7, 10, 1);
  ctx.fillStyle = "#cfe6b0"; // stitch flecks
  ctx.fillRect(5, 5, 1, 1); ctx.fillRect(8, 5, 1, 1); ctx.fillRect(11, 5, 1, 1);
  ctx.fillRect(5, 10, 1, 1); ctx.fillRect(8, 10, 1, 1); ctx.fillRect(11, 10, 1, 1);
}

function drawLeatherTop(ctx: CanvasRenderingContext2D, armored = false): void {
  // A brown leather chest piece with shoulder pads + a center seam. Armored = the SAME icon, just a
  // deeper brown palette (no studs — "normal leather but darker").
  const rim = armored ? "#3a2412" : "#5a3a22";
  const body = armored ? "#5c3a20" : "#8a5a32";
  const hi = armored ? "#7a4e2c" : "#a87444";
  const stud = armored ? "#8a5e34" : "#caa06a";
  ctx.fillStyle = rim;
  ctx.fillRect(3, 4, 10, 9); ctx.fillRect(1, 5, 3, 4); ctx.fillRect(12, 5, 3, 4); // body + shoulders
  ctx.fillStyle = body;
  ctx.fillRect(4, 5, 8, 7); ctx.fillRect(2, 5, 2, 3); ctx.fillRect(12, 5, 2, 3);
  ctx.fillStyle = hi; // highlight
  ctx.fillRect(5, 5, 6, 1); ctx.fillRect(4, 6, 1, 5);
  ctx.fillStyle = armored ? "#28190d" : "#3f2917"; // center seam + collar
  ctx.fillRect(7, 5, 2, 7); ctx.fillRect(6, 4, 4, 1);
  ctx.fillStyle = stud; // rivets
  ctx.fillRect(5, 7, 1, 1); ctx.fillRect(10, 7, 1, 1); ctx.fillRect(5, 10, 1, 1); ctx.fillRect(10, 10, 1, 1);
}

function drawLeatherPants(ctx: CanvasRenderingContext2D, armored = false): void {
  // Brown leather leggings. Armored = the SAME icon in a deeper brown palette.
  const rim = armored ? "#3a2412" : "#5a3a22";
  const body = armored ? "#5c3a20" : "#8a5a32";
  const hi = armored ? "#7a4e2c" : "#a87444";
  const buckle = armored ? "#8a5e34" : "#caa06a";
  ctx.fillStyle = armored ? "#28190d" : "#3f2917"; // belt
  ctx.fillRect(3, 3, 10, 2);
  ctx.fillStyle = buckle; ctx.fillRect(7, 3, 2, 2); // buckle
  ctx.fillStyle = rim; // dark rim of the legs
  ctx.fillRect(3, 5, 4, 9); ctx.fillRect(9, 5, 4, 9);
  ctx.fillStyle = body; // leather body
  ctx.fillRect(4, 5, 3, 8); ctx.fillRect(9, 5, 3, 8);
  ctx.fillStyle = hi; // highlight stripes
  ctx.fillRect(4, 5, 1, 8); ctx.fillRect(9, 5, 1, 8);
  ctx.fillStyle = armored ? "#1f1208" : "#2e1d10"; // knee seams
  ctx.fillRect(4, 9, 3, 1); ctx.fillRect(9, 9, 3, 1);
}

function drawMetalBar(ctx: CanvasRenderingContext2D, body: string, gleam: string, shade: string): void {
  // A stubby ingot drawn in slight 3/4 view: top face (light), front face (body), shaded base.
  ctx.fillStyle = gleam; // top face
  ctx.fillRect(3, 6, 10, 3);
  ctx.fillStyle = body; // front face
  ctx.fillRect(3, 9, 10, 4);
  ctx.fillStyle = shade; // shaded base + right edge
  ctx.fillRect(3, 12, 10, 1); ctx.fillRect(12, 9, 1, 4);
  ctx.fillStyle = gleam; // top highlight streak
  ctx.fillRect(4, 6, 5, 1);
  ctx.fillStyle = shade; // top-edge bevel
  ctx.fillRect(3, 8, 10, 1);
}

function drawBlunt(ctx: CanvasRenderingContext2D): void {
  // A rolled blunt running corner-to-corner: cream paper, a darker twisted tip, a glowing
  // ember, and a little wisp of smoke curling up.
  ctx.fillStyle = "#d8c7a0"; // rolled paper
  for (let i = 0; i < 9; i++) ctx.fillRect(3 + i, 11 - i, 2, 2);
  ctx.fillStyle = "#b59a6a"; // shaded underside seam
  for (let i = 0; i < 9; i++) ctx.fillRect(3 + i, 12 - i, 1, 1);
  ctx.fillStyle = "#6f5a3a"; // twisted tip (bottom-left)
  ctx.fillRect(3, 11, 2, 2);
  ctx.fillStyle = "#3a2a1a"; // ash just behind the ember (top-right)
  ctx.fillRect(11, 3, 1, 1);
  ctx.fillStyle = "#ff7b2e"; ctx.fillRect(12, 2, 2, 2); // ember
  ctx.fillStyle = "#ffd86b"; ctx.fillRect(13, 2, 1, 1); // hot core
  ctx.fillStyle = "rgba(200,200,210,0.7)"; // smoke wisp
  ctx.fillRect(13, 1, 1, 1); ctx.fillRect(12, 0, 1, 1); ctx.fillRect(14, 0, 1, 1);
}

function drawWorms(ctx: CanvasRenderingContext2D): void {
  // An open bait tin with a couple of pink worms wriggling out of dark soil.
  ctx.fillStyle = "#8a9099"; ctx.fillRect(3, 7, 10, 6); // tin body
  ctx.fillStyle = "#b6bcc4"; ctx.fillRect(2, 6, 12, 2); // rim
  ctx.fillStyle = "#3a2c1e"; ctx.fillRect(4, 8, 8, 3);  // soil
  ctx.fillStyle = "#e08a9a"; // worms
  ctx.fillRect(5, 6, 1, 3); ctx.fillRect(6, 5, 2, 1); ctx.fillRect(8, 6, 1, 1);
  ctx.fillRect(10, 7, 1, 2); ctx.fillRect(9, 4, 1, 3);
  ctx.fillStyle = "#f2b3bf"; ctx.fillRect(6, 4, 1, 1); ctx.fillRect(9, 3, 1, 1); // worm heads
}


function drawWateringPot(ctx: CanvasRenderingContext2D): void {
  // A tin watering can: round body, top handle, long spout with a sprinkle head + water drips.
  ctx.fillStyle = "#7f96a8"; ctx.fillRect(5, 7, 7, 6); // body
  ctx.fillStyle = "#a5bccb"; ctx.fillRect(6, 8, 2, 3); // sheen
  ctx.fillStyle = "#5a6f80"; ctx.fillRect(5, 12, 7, 1); ctx.fillRect(11, 7, 1, 6); // base + side shade
  ctx.fillStyle = "#8fa6b8"; ctx.fillRect(6, 5, 5, 1); ctx.fillRect(6, 6, 1, 1); ctx.fillRect(10, 6, 1, 1); // handle
  ctx.fillStyle = "#7f96a8"; // spout
  ctx.fillRect(4, 8, 1, 1); ctx.fillRect(3, 7, 1, 1); ctx.fillRect(2, 6, 1, 1);
  ctx.fillStyle = "#a5bccb"; ctx.fillRect(1, 5, 2, 1); // sprinkle head
  ctx.fillStyle = "#86d4f4"; // drips
  ctx.fillRect(1, 7, 1, 1); ctx.fillRect(0, 9, 1, 1); ctx.fillRect(2, 9, 1, 1);
}


function drawRollingPaper(ctx: CanvasRenderingContext2D): void {
  // An angled leaf of thin white paper: diagonal sheet, amber gum strip along the top edge,
  // a soft crease down the middle, shaded lower edge.
  ctx.fillStyle = "#efe9d8";
  for (let i = 0; i < 9; i++) ctx.fillRect(2 + i, 10 - i, 4, 3); // the sheet
  ctx.fillStyle = "#d8b86a";
  for (let i = 0; i < 9; i++) ctx.fillRect(2 + i, 10 - i, 1, 1); // gum strip (top edge)
  ctx.fillStyle = "#b8b09a";
  for (let i = 0; i < 9; i++) ctx.fillRect(4 + i, 11 - i, 1, 1); // centre crease
  ctx.fillStyle = "#cfc7b0";
  for (let i = 0; i < 9; i++) ctx.fillRect(5 + i, 12 - i, 1, 1); // shaded lower edge
}

function drawPotato(ctx: CanvasRenderingContext2D): void {
  // A lumpy raw potato with a couple of eyes.
  ctx.fillStyle = "#a87a44"; ctx.fillRect(4, 5, 9, 7); ctx.fillRect(5, 4, 7, 1); ctx.fillRect(5, 12, 8, 1); ctx.fillRect(3, 7, 1, 3); ctx.fillRect(13, 6, 1, 4); // body
  ctx.fillStyle = "#c2974f"; ctx.fillRect(5, 5, 4, 2); // highlight
  ctx.fillStyle = "#7a5630"; ctx.fillRect(6, 9, 2, 1); ctx.fillRect(10, 7, 1, 1); ctx.fillRect(9, 10, 1, 1); // eyes/dimples
}

function drawHogBurger(ctx: CanvasRenderingContext2D): void {
  // A bigger, meatier burger: toasted top bun, lettuce, a thick double hog patty, cheese, bottom bun.
  ctx.fillStyle = "#c8893f"; ctx.fillRect(2, 2, 12, 3); ctx.fillRect(3, 1, 10, 1); // toasted top bun dome
  ctx.fillStyle = "#e7c184"; ctx.fillRect(4, 2, 1, 1); ctx.fillRect(7, 2, 1, 1); ctx.fillRect(10, 2, 1, 1); // sesame
  ctx.fillStyle = "#6fbf4a"; ctx.fillRect(1, 5, 14, 1); // lettuce
  ctx.fillStyle = "#f2b13a"; ctx.fillRect(2, 6, 12, 1); // cheese
  ctx.fillStyle = "#5e3220"; ctx.fillRect(2, 7, 12, 4); // thick hog patty (double)
  ctx.fillStyle = "#3e2014"; ctx.fillRect(2, 9, 12, 1); // patty seam
  ctx.fillStyle = "#7a4326"; ctx.fillRect(1, 7, 1, 4); ctx.fillRect(14, 7, 1, 4); // charred edges
  ctx.fillStyle = "#c8893f"; ctx.fillRect(2, 11, 12, 3); // bottom bun
  ctx.fillStyle = "#a86f30"; ctx.fillRect(2, 13, 12, 1);
}

function drawFries(ctx: CanvasRenderingContext2D): void {
  // A red carton with golden fries poking out.
  ctx.fillStyle = "#f5c542"; // fries
  for (const x of [5, 7, 9, 11]) { ctx.fillRect(x, 2, 1, 7); }
  ctx.fillStyle = "#ffe08a"; ctx.fillRect(5, 2, 1, 2); ctx.fillRect(9, 2, 1, 2);
  ctx.fillStyle = "#d23a3a"; // carton
  ctx.fillRect(3, 8, 10, 6);
  ctx.fillStyle = "#b02a2a"; ctx.fillRect(3, 8, 10, 1);
  ctx.fillStyle = "#f2f2f5"; ctx.fillRect(6, 10, 4, 2); // label patch
}

function drawMeat(ctx: CanvasRenderingContext2D): void {
  // A raw meat cut with a white bone nub.
  ctx.fillStyle = "#c0563f"; ctx.fillRect(4, 5, 9, 8); ctx.fillRect(5, 4, 7, 1); ctx.fillRect(5, 13, 7, 1); // flesh
  ctx.fillStyle = "#d97a64"; ctx.fillRect(5, 6, 6, 3);                 // highlight
  ctx.fillStyle = "#a03b2a"; ctx.fillRect(6, 10, 6, 2);               // shadow/marbling
  ctx.fillStyle = "#f2ece0"; ctx.fillRect(2, 7, 3, 4); ctx.fillRect(1, 8, 2, 2); // bone nub
}

function drawCharm(ctx: CanvasRenderingContext2D): void {
  // A bone tusk charm hung on a cord.
  ctx.fillStyle = "#6a5230"; for (let i = 0; i < 7; i++) ctx.fillRect(3 + i, 2 + (i < 4 ? i : 6 - i), 1, 1); // cord
  ctx.fillStyle = "#efe7cf"; // ivory tusk
  ctx.fillRect(7, 5, 3, 3); ctx.fillRect(8, 7, 3, 3); ctx.fillRect(9, 9, 2, 3); ctx.fillRect(10, 11, 2, 2);
  ctx.fillStyle = "#fff8e6"; ctx.fillRect(7, 5, 1, 2);               // gleam
  ctx.fillStyle = "#cbbf9e"; ctx.fillRect(10, 12, 2, 1);            // tip shade
}

function drawTusk(ctx: CanvasRenderingContext2D): void {
  // A curved ivory tusk (corner to corner).
  ctx.fillStyle = "#efe7cf";
  ctx.fillRect(3, 12, 3, 2); ctx.fillRect(5, 10, 3, 2); ctx.fillRect(7, 8, 3, 2); ctx.fillRect(9, 6, 2, 2); ctx.fillRect(10, 4, 2, 2); ctx.fillRect(11, 3, 2, 1);
  ctx.fillStyle = "#fff8e6"; ctx.fillRect(4, 12, 1, 1); ctx.fillRect(6, 10, 1, 1); // gleams
  ctx.fillStyle = "#cbbf9e"; ctx.fillRect(3, 13, 3, 1);             // base shade
}

function drawBook(ctx: CanvasRenderingContext2D): void {
  // A green hardcover book with cream pages + a small "run" mark.
  ctx.fillStyle = "#2f7a46"; ctx.fillRect(3, 2, 10, 12);            // cover
  ctx.fillStyle = "#256038"; ctx.fillRect(3, 2, 2, 12);            // spine
  ctx.fillStyle = "#efe7cf"; ctx.fillRect(5, 3, 7, 10);           // pages
  ctx.fillStyle = "#c9bf9e"; for (let y = 4; y < 13; y += 2) ctx.fillRect(6, y, 5, 1); // page lines
  ctx.fillStyle = "#2f7a46"; ctx.fillRect(8, 6, 3, 1); ctx.fillRect(9, 7, 2, 1); ctx.fillRect(8, 8, 3, 1); // ">" run chevrons
}

function drawDrink(ctx: CanvasRenderingContext2D, liquid: string, gleam: string): void {
  // A cup with a lid + straw; `liquid` tints the cup (blue water / red soda).
  ctx.fillStyle = "#e8e8ec"; ctx.fillRect(4, 5, 8, 9); // cup body
  ctx.fillStyle = liquid; ctx.fillRect(5, 6, 6, 7); // liquid window
  ctx.fillStyle = gleam; ctx.fillRect(5, 6, 1, 7); // gleam
  ctx.fillStyle = "#c8ccd2"; ctx.fillRect(4, 4, 8, 2); // lid
  ctx.fillStyle = "#ff5a8a"; ctx.fillRect(9, 1, 1, 4); // straw
  ctx.fillStyle = "#cfd3d8"; ctx.fillRect(4, 13, 8, 1); // base shadow
}

function drawMagicDust(ctx: CanvasRenderingContext2D): void {
  // A mound of glowing magic dust — a purple pile topped with bright cyan/white sparkles.
  ctx.fillStyle = "#5a2f9a"; ctx.fillRect(4, 11, 8, 3); ctx.fillRect(5, 10, 6, 1); // mound base
  ctx.fillStyle = "#8a4fd0"; ctx.fillRect(5, 11, 5, 1);                            // mound mid
  ctx.fillStyle = "#b985f0"; ctx.fillRect(5, 12, 3, 1);                            // mound highlight
  const spark = (x: number, y: number, c: string): void => { ctx.fillStyle = c; ctx.fillRect(x, y - 1, 1, 3); ctx.fillRect(x - 1, y, 3, 1); };
  spark(7, 4, "#aef6ff"); spark(11, 6, "#ffffff"); spark(4, 7, "#e0c2ff"); spark(9, 8, "#aef6ff"); // sparkles
}

/** A side-on fish facing LEFT (head left, tail right), built from a body ellipse + tail/fins/eye.
 *  `p` is the palette; `opts` adds a dorsal fin, spots, or an ethereal glow for rarer fish. */
function drawFish(
  ctx: CanvasRenderingContext2D,
  p: { body: string; belly: string; dark: string; fin: string; gleam: string },
  opts: { dorsal?: boolean; spots?: string; glow?: string } = {},
): void {
  // body ellipse (head ~x2, tail ~x12)
  ctx.fillStyle = p.body;
  for (const [y, a, b] of [[6, 4, 9], [7, 3, 11], [8, 2, 12], [9, 2, 12], [10, 3, 11], [11, 4, 9]]) ctx.fillRect(a, y, b - a + 1, 1);
  ctx.fillStyle = p.belly; ctx.fillRect(3, 10, 8, 1); ctx.fillRect(4, 11, 5, 1); // lighter belly
  ctx.fillStyle = p.dark; ctx.fillRect(3, 6, 7, 1); ctx.fillRect(2, 8, 1, 2);    // back shade + nose
  // tail fin (right)
  ctx.fillStyle = p.fin;
  ctx.fillRect(12, 7, 2, 1); ctx.fillRect(13, 6, 2, 1); ctx.fillRect(13, 11, 2, 1); ctx.fillRect(12, 10, 2, 1); ctx.fillRect(13, 8, 1, 2);
  if (opts.dorsal) { ctx.fillStyle = p.fin; ctx.fillRect(6, 5, 4, 1); ctx.fillRect(7, 4, 2, 1); } // dorsal fin
  ctx.fillStyle = p.fin; ctx.fillRect(7, 11, 2, 2); // pectoral fin
  ctx.fillStyle = "#16181c"; ctx.fillRect(4, 8, 2, 2); // eye
  ctx.fillStyle = p.gleam; ctx.fillRect(4, 8, 1, 1);   // eye gleam
  ctx.fillStyle = p.dark; ctx.fillRect(2, 9, 1, 1);    // mouth
  if (opts.spots) { ctx.fillStyle = opts.spots; ctx.fillRect(7, 7, 1, 1); ctx.fillRect(9, 9, 1, 1); ctx.fillRect(8, 8, 1, 1); }
  if (opts.glow) {
    ctx.fillStyle = opts.glow; ctx.fillRect(10, 6, 1, 1); ctx.fillRect(11, 9, 1, 1); ctx.fillRect(6, 11, 1, 1);
    ctx.fillStyle = "#ffffff"; ctx.fillRect(9, 7, 1, 1);
  }
}

function drawRod(ctx: CanvasRenderingContext2D): void {
  // A diagonal tan rod (SW→NE) with a dark reel near the grip and a line + hook hanging from the tip.
  ctx.fillStyle = "#8a5a30"; for (let i = 0; i < 11; i++) ctx.fillRect(3 + i, 13 - i, 1, 1); // shaft
  ctx.fillStyle = "#a9743c"; for (let i = 0; i < 11; i++) ctx.fillRect(4 + i, 13 - i, 1, 1); // shaft highlight
  ctx.fillStyle = "#6e451f"; ctx.fillRect(13, 2, 2, 2);                                       // rod tip
  ctx.fillStyle = "#3a3d42"; ctx.fillRect(4, 11, 3, 3); ctx.fillStyle = "#5a5e63"; ctx.fillRect(5, 12, 1, 1); // reel
  ctx.fillStyle = "#c8ccd2"; ctx.fillRect(14, 3, 1, 4); ctx.fillRect(14, 7, 1, 3);            // line
  ctx.fillStyle = "#9aa0a6"; ctx.fillRect(13, 10, 1, 1); ctx.fillRect(13, 11, 1, 1); ctx.fillRect(12, 11, 1, 1); // hook
}

function drawBmxIcon(ctx: CanvasRenderingContext2D): void {
  // A side-on BMX: two spoked wheel rings joined by a red frame, fork, saddle + handlebar.
  const px = (x: number, y: number, c: string): void => { ctx.fillStyle = c; ctx.fillRect(x, y, 1, 1); };
  const line = (x0: number, y0: number, x1: number, y1: number, c: string): void => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let i = 0; i <= n; i++) px(Math.round(x0 + ((x1 - x0) * i) / n), Math.round(y0 + ((y1 - y0) * i) / n), c);
  };
  const wheel = (cx: number, cy: number): void => {
    const ring: ReadonlyArray<[number, number]> = [[-3, -1], [-3, 0], [-3, 1], [-2, -2], [-2, 2], [-1, -3], [-1, 3], [0, -3], [0, 3], [1, -3], [1, 3], [2, -2], [2, 2], [3, -1], [3, 0], [3, 1]];
    for (const [dx, dy] of ring) px(cx + dx, cy + dy, "#8d949e");
    px(cx, cy, "#d6dade"); // hub
  };
  wheel(4, 11); wheel(11, 11);
  line(4, 11, 7, 11, "#c23b3b");   // chain stay
  line(7, 11, 5, 6, "#c23b3b");    // seat tube
  line(7, 11, 10, 5, "#c23b3b");   // down tube
  line(5, 6, 9, 5, "#c23b3b");     // top tube
  line(4, 11, 5, 7, "#e0685a");    // seat stay (lighter red)
  line(10, 5, 11, 11, "#b3b9c0");  // fork
  ctx.fillStyle = "#e0e4e8"; ctx.fillRect(3, 5, 3, 1);   // saddle
  px(10, 4, "#b3b9c0"); ctx.fillStyle = "#e0e4e8"; ctx.fillRect(9, 3, 3, 1); // stem + bar
  px(8, 12, "#d6dade"); // pedal
}

function drawUnknown(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = "#4a2a82";
  ctx.fillRect(2, 2, 12, 12);
  ctx.fillStyle = "#c4b5fd";
  ctx.font = "10px monospace";
  ctx.fillText("?", 5, 12);
}

function drawBed(ctx: CanvasRenderingContext2D): void {
  // A side-view single bed: dark wood frame + headboard, pale mattress, red blanket, white pillow.
  ctx.fillStyle = "#4a3320"; ctx.fillRect(1, 4, 2, 9);                       // headboard (left)
  ctx.fillStyle = "#33241a"; ctx.fillRect(1, 11, 14, 2);                     // frame rail
  ctx.fillRect(2, 13, 2, 2); ctx.fillRect(12, 13, 2, 2);                     // legs
  ctx.fillStyle = "#e8e2d4"; ctx.fillRect(3, 8, 12, 3);                      // mattress
  ctx.fillStyle = "#f4f0e6"; ctx.fillRect(3, 6, 4, 3);                       // pillow
  ctx.fillStyle = "#8a3a3a"; ctx.fillRect(7, 7, 8, 4);                       // blanket
  ctx.fillStyle = "#6e2e2e"; ctx.fillRect(7, 10, 8, 1);                      // blanket shade
}

function drawFurnace(ctx: CanvasRenderingContext2D): void {
  // A brick smelting furnace: stone plinth, brick body with mortar lines, chimney stub, and a
  // glowing fire mouth at the base.
  ctx.fillStyle = "#5d5f66"; ctx.fillRect(10, 1, 3, 3);                      // chimney stub
  ctx.fillStyle = "#6b3d2f"; ctx.fillRect(3, 4, 10, 2);                      // tapered crown
  ctx.fillStyle = "#7a4636"; ctx.fillRect(2, 6, 12, 7);                      // brick body
  ctx.fillStyle = "#5e3428";                                                 // mortar lines
  ctx.fillRect(2, 8, 12, 1); ctx.fillRect(2, 11, 12, 1);
  ctx.fillRect(6, 6, 1, 2); ctx.fillRect(10, 9, 1, 2);
  ctx.fillStyle = "#1a0e08"; ctx.fillRect(5, 9, 6, 4);                       // fire mouth
  ctx.fillStyle = "#ff6a1a"; ctx.fillRect(6, 10, 4, 3);                      // flames
  ctx.fillStyle = "#ffc23a"; ctx.fillRect(7, 11, 2, 2);                      // flame core
  ctx.fillStyle = "#55575e"; ctx.fillRect(1, 13, 14, 2);                     // stone plinth
}

function drawAnvil(ctx: CanvasRenderingContext2D): void {
  // A steel anvil on a wooden stump: wide face with a horn to the right, narrow waist, flared base.
  ctx.fillStyle = "#4c505a"; ctx.fillRect(2, 4, 10, 3);                      // face
  ctx.fillRect(12, 5, 2, 2); ctx.fillRect(14, 5, 1, 1);                      // horn taper
  ctx.fillStyle = "#6a707c"; ctx.fillRect(2, 4, 10, 1);                      // top gleam
  ctx.fillStyle = "#33363c"; ctx.fillRect(5, 7, 4, 3);                       // waist
  ctx.fillStyle = "#3a3d44"; ctx.fillRect(4, 10, 7, 2);                      // base
  ctx.fillStyle = "#4a3320"; ctx.fillRect(3, 12, 9, 3);                      // wooden stump
  ctx.fillStyle = "#33241a"; ctx.fillRect(3, 14, 9, 1);                      // stump shade
}

function drawSafebox(ctx: CanvasRenderingContext2D): void {
  // A squat steel safe: dark body, lighter door plate, brass dial + handle, feet.
  ctx.fillStyle = "#24262b"; ctx.fillRect(2, 2, 12, 12);                     // body outline
  ctx.fillStyle = "#3a3d44"; ctx.fillRect(3, 3, 10, 10);                     // body
  ctx.fillStyle = "#4a4e57"; ctx.fillRect(4, 4, 8, 8);                       // door plate
  ctx.fillStyle = "#b9892f"; ctx.fillRect(6, 7, 2, 2);                       // dial
  ctx.fillRect(10, 5, 1, 5);                                                 // handle bar
  ctx.fillStyle = "#15171b"; ctx.fillRect(3, 14, 2, 1); ctx.fillRect(11, 14, 2, 1); // feet
}
